import Oidc from 'oidc-client';
import appBaseUrl from '@/tools/appBaseUrl';

const POST_LOGOUT_REDIRECT_URI_KEY = 'post_logout_redirect_uri';
const checkLogoutRedirect = () => {
    const uri = window.localStorage.getItem(POST_LOGOUT_REDIRECT_URI_KEY);
    if (uri) {
        window.localStorage.removeItem(POST_LOGOUT_REDIRECT_URI_KEY);
        window.location = uri;
    }
};
const setLogoutRedirect = (post_logout_redirect_uri) => {
    window.localStorage.setItem(POST_LOGOUT_REDIRECT_URI_KEY, post_logout_redirect_uri || '/');
};

const mgr = new Oidc.UserManager({
    userStore: new Oidc.WebStorageStateStore({ store: window.localStorage }),
    loadUserInfo: true,
    filterProtocolClaims: true,
    authority: `https://identity.${window.rsvDomain}`,
    client_id: 'rsv-portal',
    redirect_uri: `${appBaseUrl}web-callback.html`,
    response_type: 'code',
    scope: 'openid profile',
    post_logout_redirect_uri: appBaseUrl,
    silent_redirect_uri: `${appBaseUrl}web-silent-renew.html`,
    automaticSilentRenew: true,
    clockSkew: 60 * 60 * 24,
});

export class SecurityService {

    constructor(mgr, { debug = false } = {}) {
        this.mgr = mgr;
        this.listeners = [];
        this.checkAuthPromise = null;
        this.debug = debug;
        this._setupEvents();
    }

    registerServices(callbacks, ctx) {
        callbacks.forEach(cb => cb(this, ctx));
        return this;
    }

    addUserListener(listener) {
        this.listeners = [
            ...this.listeners,
            listener,
        ];
        return this;
    }

    _notifyListeners(user) {
        this.listeners.forEach(listener => listener(user));
        this.debug && console.log('[DEBUG] All SecirityService listeners notified', user);
    }

    async getUser() {
        try {
            return await this.mgr.getUser();
        } catch (e) {
            return null;
        }
    }

    _setupEvents() {
        this.mgr.events.addUserLoaded(async () => {
            this.debug && console.log('[DEBUG] UserLoaded');
            this._notifyListeners(await this.getUser());
        });
        this.mgr.events.addUserUnloaded(() => {
            this.debug && console.log('[DEBUG] UserUnloaded');
        });
        this.mgr.events.addAccessTokenExpiring(() => {
            this.debug && console.log('[DEBUG] AccessTokenExpiring');
        });
        this.mgr.events.addAccessTokenExpired(() => {
            this.debug && console.log('[DEBUG] AccessTokenExpired');
            this.signinSilent();
        });
        this.mgr.events.addSilentRenewError(error => {
            this.debug && console.log('[DEBUG] SilentRenewError', error);
        });
        this.mgr.events.addUserSignedIn(() => {
            this.debug && console.log('[DEBUG] UserSignedIn');
        });
        this.mgr.events.addUserSignedOut(async () => {
            this.debug && console.log('[DEBUG] UserSignedOut');
            await this.removeUser();
            this._notifyListeners(null);
        });
        this.mgr.events.addUserSessionChanged(() => {
            this.debug && console.log('[DEBUG] UserSessionChanged');
        });
    }

    async checkAuth({ requiresAuth = false } = {}) {
        if (this.checkAuthPromise) {
            this.debug && console.log('[DEBUG] SecirityService already has checkAuthPromise');
            return this.checkAuthPromise;
        }
        this.debug && console.log('[DEBUG] SecirityService checkAuth', { requiresAuth });
        this.checkAuthPromise = this._makeCheckAuthPromise({ requiresAuth });
        let user = await this.checkAuthPromise;
        this._notifyListeners(user);
        this.checkAuthPromise = null;
        return user;
    }

    async _makeCheckAuthPromise({ requiresAuth }) {
        let user = await this.getUser();

        if (!user || user.expired) {
            user = await this.signinSilent();
            if (user === null && requiresAuth) {
                await this.mgr.signinRedirect({ data: { path: window.location.pathname +  window.location.search } });
            }
        }
        return user;
    }
    signIn() {
        console.warn('Implicit call of requireAuth');
        this.checkAuth({ requiresAuth: true });
    }
    signOut(post_logout_redirect_uri = null) {
        setLogoutRedirect(post_logout_redirect_uri);
        this.mgr.signoutRedirect({ post_logout_redirect_uri }).then(resp => {
            this.debug && console.log('[DEBUG] signed out', resp);
        }).catch(err => {
            this.debug && console.log('[DEBUG] signOut error', err);
        });
    }
    async signinSilent() {
        try {
            let user = await this.mgr.signinSilent();
            this.debug && console.log('[DEBUG] signin silent', user);
            return user;
        } catch (err) {
            this.debug && console.log('[DEBUG] signin silent error', err);
            return null;
        }
    }
    removeUser() {
        this.mgr.removeUser().then(() => {
            this.debug && console.log('[DEBUG] user removed from storage');
        }).catch(err => {
            this.debug && console.log('[DEBUG] remove user error', err);
        });
    }
    signinRedirectCallback() {
        return this.mgr.signinRedirectCallback();
    }
}

const debug = process.env.NODE_ENV === 'development' || false;

let securityService = new SecurityService(mgr, { debug });

Oidc.Log.logger = console;
Oidc.Log.level = Oidc.Log.INFO;

checkLogoutRedirect();

export { securityService };
